/**
 * Vocabulario del dominio. Es la única fuente de estos valores: la base los guarda como
 * texto y los valida con CHECK contra estas mismas listas (ver packages/db).
 */

/** Etapas de la ruta de aprobación, en orden. */
export const ETAPAS = ["elaboro", "reviso", "aprobo"] as const;
export type Etapa = (typeof ETAPAS)[number];

export const NOMBRE_ETAPA: Record<Etapa, string> = {
  elaboro: "Elaboró",
  reviso: "Revisó",
  aprobo: "Aprobó",
};

/** Estados de una revisión (docs/diagramas/02-ciclo-de-vida.mmd). */
export const ESTADOS_REVISION = [
  "borrador",
  "en_revision",
  "esperando_aprobacion",
  "vigente",
  "obsoleta",
  "dispuesto",
] as const;
export type EstadoRevision = (typeof ESTADOS_REVISION)[number];

export const NOMBRE_ESTADO: Record<EstadoRevision, string> = {
  borrador: "Borrador",
  en_revision: "En revisión",
  esperando_aprobacion: "Esperando aprobación",
  vigente: "Vigente",
  obsoleta: "Obsoleta",
  dispuesto: "Dispuesto",
};

/**
 * Rol global del usuario, independiente del proceso. Se llama por lo que permite, no por el
 * área que lo usa (decidido el 2026-10-19: "Calidad" es un puesto, y el puesto ya va aparte en
 * `usuarios.puesto`). `administrador` administra el sistema de gestión: usuarios, catálogos,
 * alertas, formatos en papel, historial importado, retiros y la única autoridad sobre el nivel
 * 4 (hasta ese día se llamaba `calidad`). `soporte` es Soporte Tecnológico: el único que carga
 * y cambia las imágenes de firma del personal y ve la IP de quien firmó (antes `ti`).
 */
export const ROLES_GLOBALES = ["colaborador", "gerencia", "administrador", "soporte"] as const;
export type RolGlobal = (typeof ROLES_GLOBALES)[number];

/** Cómo se llama cada rol en pantalla (selector de Usuarios, "Mi cuenta"). */
export const NOMBRE_ROL: Record<RolGlobal, string> = {
  colaborador: "Colaborador",
  gerencia: "Gerencia",
  administrador: "Administrador",
  soporte: "Soporte Tecnológico",
};

/** Rol de un usuario dentro de un proceso: el punto verde o gris del sidebar. */
export const ROLES_PROCESO = ["carga", "consulta"] as const;
export type RolProceso = (typeof ROLES_PROCESO)[number];

/** Niveles de protección, con la redacción heredada de CSGestión. */
export const NIVELES_PROTECCION = [1, 2, 3, 4] as const;
export type NivelProteccion = (typeof NIVELES_PROTECCION)[number];

export const NIVEL_PROTECCION: Record<NivelProteccion, { nombre: string; quienVe: string }> = {
  1: { nombre: "Público", quienVe: "Personal interno y externos" },
  2: { nombre: "Uso general", quienVe: "Personal involucrado en la actividad" },
  3: { nombre: "Restringido", quienVe: "Dueños del proceso y gestión" },
  4: { nombre: "Confidencial", quienVe: "Solo personas autorizadas por el administrador" },
};

/** Manejo del documento: dónde vive la copia oficial. */
export const MANEJOS = ["digital", "fisico", "fisico_y_digital"] as const;
export type Manejo = (typeof MANEJOS)[number];

export const NOMBRE_MANEJO: Record<Manejo, string> = {
  digital: "Solo digital",
  fisico: "Solo físico",
  fisico_y_digital: "Físico y digital",
};

/** Disposición de un registro al cumplir su retención. */
export const DISPOSICIONES = ["destruccion", "archivo_pasivo"] as const;
export type Disposicion = (typeof DISPOSICIONES)[number];

export const NOMBRE_DISPOSICION: Record<Disposicion, string> = {
  destruccion: "Destrucción",
  archivo_pasivo: "Archivo pasivo",
};

/** Por dónde entró la acción. Se guarda en firmas, acuses y auditoría. */
export const CANALES = ["web", "movil", "sistema"] as const;
export type Canal = (typeof CANALES)[number];

/**
 * Qué le avisa la campana a una persona (fase 2). `publicacion` trae un acuse de lectura
 * pendiente salvo que el documento sea un registro.
 */
export const TIPOS_NOTIFICACION = [
  "publicacion",
  "firma_pendiente",
  "devolucion",
  "alerta",
  "encuesta",
] as const;
export type TipoNotificacion = (typeof TIPOS_NOTIFICACION)[number];

/**
 * En qué va el correo de un aviso (fase 2). `omitido` no es un fallo: es que no había que
 * mandarlo (no hay SMTP configurado, o la persona ya no está activa), y por eso se distingue de
 * `fallido`, que sí hay que mirar.
 *
 * `enviando` es la reserva: el despachador la escribe **antes** de abrir la conexión SMTP, y por
 * eso ninguna otra corrida puede tomar esa fila. Si el proceso muere con la fila así, nadie sabe
 * si el correo alcanzó a salir; la fila se da por perdida (`fallido`) en vez de reintentarse,
 * porque de las dos formas de equivocarse (no mandarlo o mandarlo dos veces) la segunda es la
 * que le quita credibilidad a un sistema de calidad. El aviso sigue en la campana igual.
 */
export const ESTADOS_CORREO = ["pendiente", "enviando", "enviado", "omitido", "fallido"] as const;

/** Tras cuántos intentos fallidos se deja de reintentar un correo y queda como `fallido`. */
export const INTENTOS_CORREO = 5;

/**
 * Los interruptores de canal del panel *Cómo y cuándo avisar* (fase 2): por cada tipo de aviso,
 * si además del aviso en la app sale un correo. Solo un administrador los cambia.
 *
 * El interruptor apaga **el correo, no el aviso**: la campana sigue avisando siempre. Por eso no
 * hay interruptor para apagar la campana (un documento controlado se distribuye, no se opta por
 * no enterarse) y por eso todos nacen encendidos.
 */
export const CANALES_CORREO_POR_DEFECTO: Record<TipoNotificacion, boolean> = {
  publicacion: true,
  firma_pendiente: true,
  devolucion: true,
  alerta: true,
  encuesta: true,
};
export type CanalesCorreo = Record<TipoNotificacion, boolean>;

export const NOMBRE_CANAL_CORREO: Record<TipoNotificacion, string> = {
  publicacion: "Publicación y acuse de lectura",
  firma_pendiente: "Firma pendiente y recordatorios",
  devolucion: "Devolución con observaciones",
  alerta: "Alertas de vencimiento",
  encuesta: "Encuestas por responder",
};

/** Origen de una revisión: creada en la app o traída del sistema anterior. */
export const ORIGENES_REVISION = ["app", "importado"] as const;

/** Zona horaria de presentación y de los jobs programados. Honduras no cambia de hora. */
export const ZONA_HORARIA = "America/Tegucigalpa";

/**
 * Situación de un documento en la lista maestra (pantalla 01). Combina el estado de la
 * revisión en curso con las alertas de tiempo: "Revisión vencida" y "Retención por vencer" no
 * son estados de la máquina, son avisos sobre una revisión vigente.
 */
export const SITUACIONES = [
  "borrador",
  "en_revision",
  "esperando_aprobacion",
  "vigente",
  "revision_vencida",
  "retencion_por_vencer",
  "sin_vigente",
  "dispuesto",
  "retirado",
] as const;
export type Situacion = (typeof SITUACIONES)[number];

export const NOMBRE_SITUACION: Record<Situacion, string> = {
  borrador: "Borrador",
  en_revision: "En revisión",
  esperando_aprobacion: "Esperando aprobación",
  vigente: "Vigente",
  revision_vencida: "Revisión vencida",
  retencion_por_vencer: "Retención por vencer",
  sin_vigente: "Sin revisión vigente",
  dispuesto: "Dispuesto",
  /** Fase 6: un administrador lo retiró del sistema de gestión; queda para consulta. */
  retirado: "Retirado",
};

/**
 * Lo que la API pone en `?error=` al devolver a /login a alguien con credenciales correctas que
 * no puede entrar: desde fuera sin acceso externo, o con el usuario desactivado (fase 6).
 */
export const MOTIVO_RECHAZO = {
  sinAccesoExterno: "sin_acceso_externo",
  usuarioDesactivado: "usuario_desactivado",
} as const;

/** Con cuánta anticipación avisa la alerta de retención (pantalla 02: "un mes antes"). */
export const DIAS_AVISO_RETENCION = 30;
